import React, { useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { useTranslate } from 'react-admin';
import { filterMenuItems, getBlockMenuItems, type BlockMenuItem } from './blockMenuItems';
import { CATEGORY_ORDER } from './blockRegistry';
import { insertBlockAfterSelection } from './BlockEditorToolbar';

/**
 * Right-hand block library: searchable list of insertable blocks grouped by
 * category (ported from Scilora block-library-panel).
 */
export const BlockLibraryPanel: React.FC<{ editor: Editor; onClose: () => void }> = ({ editor, onClose }) => {
    const translate = useTranslate();
    const [query, setQuery] = useState('');
    const items = useMemo(() => filterMenuItems(getBlockMenuItems(translate), query), [translate, query]);
    const grouped = useMemo(() => {
        const groups: Record<string, BlockMenuItem[]> = {};
        for (const item of items) (groups[item.category] ??= []).push(item);
        return groups;
    }, [items]);

    return (
        <Box className="oa-block-library" role="complementary" aria-label={translate('blockEditor.library.title')}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: '1px solid var(--oa-divider)' }}>
                <Typography variant="subtitle2">{translate('blockEditor.library.title')}</Typography>
                <IconButton size="small" onClick={onClose} aria-label={translate('blockEditor.library.close')}><Close fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--oa-divider)' }}>
                <TextField
                    size="small"
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={translate('blockEditor.library.search')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                />
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                {items.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        {translate('blockEditor.library.empty', { query })}
                    </Typography>
                )}
                {CATEGORY_ORDER.map((category) => {
                    const list = grouped[category];
                    if (!list || list.length === 0) return null;
                    return (
                        <Box key={category} sx={{ mb: 1.5 }}>
                            <Typography variant="overline" sx={{ px: 1, display: 'block', color: 'var(--oa-text-2)' }}>
                                {translate(`blockEditor.categories.${category}`)}
                            </Typography>
                            {list.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <button key={item.key} type="button" className="oa-library-item" onClick={() => insertBlockAfterSelection(editor, item)}>
                                        <span className="oa-library-icon"><Icon fontSize="small" /></span>
                                        <span className="oa-slash-text">
                                            <span className="oa-slash-label">{item.label}</span>
                                            <span className="oa-slash-desc">{item.description}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 1, borderTop: '1px solid var(--oa-divider)', textAlign: 'center' }}>
                {translate('blockEditor.library.hint')}
            </Typography>
        </Box>
    );
};
